// @flow

import React, { useState, useEffect, useCallback } from 'react';
import Box from '@material-ui/core/Box';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MyStore from './MyStore';
import AttribsTable from './AttribsTable';
import getHeadCoachInfo from '../api/getheadCoachInfo';
import getOrgs from '../api/getOrgs';
import { getOrgTeams } from '../api/getOrgTeams';
import Config from './../../../api/config';
import NoAccess from './NoAccess';
import { TYPES as PROGRAM_TYPES } from '../../../state/reducer/calendar/programTypes';

const PAGE_KEY = 'My Store | Marketing Links';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250
        }
    }
};

export default function ShareLinks() {
    const [headCoachInfo, setHeadCoachInfo] = useState(null);
    const [products, setProducts] = useState([]);
    const [shareItem, setShareItem] = useState(null);
    const [viewCheckoutLinks, setViewCheckoutLinks] = useState(false);
    const [shareItemUrl, setShareItemUrl] = useState(null);
    const [orgDetails, setOrgDetails] = useState(null);
    const urlPrefix = Config.URL_MARKETPLACE;

    const transformProducts = (products: array) => {
        let filtered = [];
        products.forEach((product: object) => {
            const isMarketplaceVisible = getisMarketplaceVisible(product);
            filtered.push({
                label: product.title,
                isMarketplaceVisible,
                slug: product.slug,
                type: product.type === PROGRAM_TYPES.TEAM ? 'team' : 'program',
                productId: product.id
            });
        });
        filtered.sort((a: object, b: object) => {
            return a.isMarketplaceVisible === b.isMarketplaceVisible
                ? 0
                : a.isMarketplaceVisible
                    ? -1
                    : 1;
        });
        filtered.unshift({
            label: 'My Brand',
            slug: 'my-brand',
            type: 'org'
        });
        return filtered;
    };

    const getisMarketplaceVisible = (product: any) => {
        return !product.marketplace_hidden;
    };

    useEffect(
        () => {
            const getCoachOrgDetails = async () => {
                if (headCoachInfo) {
                    return;
                }
                const response = await getHeadCoachInfo();
                if (!response) {
                    return;
                }
                setHeadCoachInfo(response);
            };
            getCoachOrgDetails();
        },
        [headCoachInfo]
    );

    useEffect(
        () => {
            const getOrgDetails = async () => {
                if (orgDetails) {
                    return;
                }
                const response = await getOrgs();
                if (!response) {
                    return;
                }
                // This component only supports 1 org.
                setOrgDetails(response[0]);
            };
            getOrgDetails();
        },
        [headCoachInfo, orgDetails]
    );

    useEffect(
        () => {
            const setProductsList = async () => {
                if (!orgDetails) {
                    return;
                }
                if (products && products.length) {
                    return;
                }
                const response = await getOrgTeams(orgDetails.id);
                if (!response.length && !response.length) {
                    return;
                }
                const transformedProducts = transformProducts(response);
                setProducts(transformedProducts);
                const productParam = window.location.search;
                if (productParam) {
                    const params = new URLSearchParams(productParam);
                    const productId = params.get('productId');
                    setShareItem(
                        transformedProducts.find(
                            (p: object) =>
                                p.productId === parseInt(productId, 10)
                        ) || transformedProducts[0]
                    );
                    return;
                }
                if (transformedProducts.length > 1) {
                    setShareItem(transformedProducts[1]);
                    return;
                }
                setShareItem(transformedProducts[0]);
            };
            setProductsList();
        },
        [products, orgDetails]
    );

    useEffect(
        () => {
            if (!shareItem) {
                return;
            }
            setShareItemUrl(getShareItemUrl(shareItem));
        },
        [shareItem, getShareItemUrl, viewCheckoutLinks]
    );

    const updateShareItem = (e: object, child: object) => {
        const selectedValue = child.props.value;
        const found = products.find((p: object) => p.slug === selectedValue);
        setShareItem(found);
    };

    const handleViewCheckoutLinks = (event: object) => {
        setViewCheckoutLinks(event.target.checked);
        setShareItem(shareItem);
        setShareItemUrl(getShareItemUrl(shareItem));
    };

    const getProductPath = (product: object) => {
        const productSlug = encodeURI(product.slug);
        return product.type === 'program'
            ? `/workout-plan/program/${productSlug}`
            : `/workout-plan/team/${productSlug}`;
    };

    const getShareItemUrl = useCallback(
        (item: object) => {
            if (orgDetails && item.type === 'org') {
                return `${urlPrefix}/brand/${encodeURIComponent(
                    orgDetails.slug
                )}`;
            }
            if (viewCheckoutLinks) {
                return `${urlPrefix}/trial/signup?team=${encodeURI(
                    item.slug
                )}&trial=true`;
            } else {
                return `${urlPrefix}${getProductPath(item)}`;
            }
        },
        [urlPrefix, orgDetails, viewCheckoutLinks]
    );

    return (
        <div>
            {headCoachInfo && headCoachInfo.orgId ? (
                <MyStore
                    pageKey={PAGE_KEY}
                    link="/mystore/attribution-links"
                    pageTitle="Marketing Links"
                    pageIntro="Use these links to ensure your sales are attributed to your marketing efforts,
                        and track which channels are working best for you on your "
                    useHeaderImage={true}
                    headerClassName="thmp-attributions-graphic"
                >
                    {headCoachInfo && shareItemUrl ? (
                        <Box>
                            <Box className="thmp-mystore-sharelinks-filter-container">
                                <Box className="thmp-mystore-sharelinks-filter">
                                    <FormControl>
                                        <InputLabel id="share-type-select-label">
                                            What are you selling?
                                        </InputLabel>
                                        <Select
                                            labelId="share-type-select-label"
                                            className="thmp-sharelinks-products-filter"
                                            MenuProps={MenuProps}
                                            label="share type"
                                            value={shareItem.slug}
                                            onChange={updateShareItem}
                                        >
                                            {products.map((product: object) => (
                                                <MenuItem
                                                    value={product.slug}
                                                    key={`item-${product.slug}`}
                                                >
                                                    {product.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                {shareItem && shareItem.type !== 'org' ? (
                                    <FormControl style={{ marginLeft: '20px' }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={viewCheckoutLinks}
                                                    name="viewCheckoutLinks"
                                                    color="primary"
                                                    onChange={
                                                        handleViewCheckoutLinks
                                                    }
                                                />
                                            }
                                            label="View checkout links"
                                        />
                                    </FormControl>
                                ) : null}
                            </Box>
                            <AttribsTable
                                orgId={headCoachInfo.orgId}
                                shareUrl={shareItemUrl}
                                productId={shareItem.productId}
                                paramSeparator={viewCheckoutLinks ? '&' : '?'}
                            />

                            <h3>Attribution Links Demo Video</h3>
                            <video
                                width="600"
                                controls={true}
                                style={{ marginBottom: '50px' }}
                            >
                                <source
                                    src="https://static.trainheroic.com/demo/attributions-help-demo_1.mp4"
                                    type="video/mp4"
                                />
                                Your browser does not support the video tag.
                            </video>
                        </Box>
                    ) : null}
                </MyStore>
            ) : (
                <NoAccess />
            )}
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/view/my-store/components/ShareLinks.js